/**
 * BasicInfoDrawer — Edit campus basic info form.
 *
 * Follows the CorporateProfilePage's BasicInfoDrawer pattern:
 *   - Pre-populates from props on open (false → true transition)
 *   - Country change resets state / zip
 *   - Validation: name required, email pattern, phone pattern, zip regex
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getCountryData, getStatesByCountry, getZipRegexByCountry } from '../../../../../libs.jsx';

const BasicInfoDrawer = ({
  open, onClose, onSave, saving,
  campusData, corporations, timezones,
}) => {
  const [campusName, setCampusName] = useState('');
  const [corporateId, setCorporateId] = useState('');
  const [country, setCountry] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [timezoneId, setTimezoneId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});

  // Populate form from props only when drawer opens (false → true)
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setCampusName(campusData?.campus_name || '');
      setCorporateId(campusData?.corporate_id?.toString() || '');
      setCountry(campusData?.campus_country || '');
      setStreetAddress(campusData?.campus_street_address || '');
      setCity(campusData?.campus_city || '');
      setState(campusData?.campus_state || '');
      setZipcode(campusData?.campus_zip || '');
      setTimezoneId(campusData?.timezone_id?.toString() || '');
      setEmail(campusData?.campus_email || '');
      setPhone((campusData?.campus_phone || '').replaceAll('-', ''));
      setErrors({});
    }
    prevOpen.current = open;
  });

  // Reset state/zip when country changes (only after initial load)
  const countryInitRef = useRef(true);
  useEffect(() => {
    if (countryInitRef.current) {
      countryInitRef.current = false;
      return;
    }
    setState('');
    setZipcode('');
  }, [country]);

  // Re-flag country init when drawer re-opens
  useEffect(() => {
    if (open) countryInitRef.current = true;
  }, [open]);

  const countries = getCountryData();
  const states = country ? (getStatesByCountry(country) || []) : [];
  const zipRegex = country ? getZipRegexByCountry(country) : '';

  const namePattern = /^[0-9a-zA-Z_\- .]+$/;
  const emailPattern = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
  const phonePattern = /^[0-9]{3}-?[0-9]{3}-?[0-9]{4}$/;

  const sortedTimezones = [...(timezones || [])].sort((a, b) =>
    (a.timezone_name || '').localeCompare(b.timezone_name || '', undefined, { sensitivity: 'base' }),
  );

  const sortedCorporations = [...(corporations || [])].sort((a, b) =>
    (a.corporate_name || '').localeCompare(b.corporate_name || '', undefined, { sensitivity: 'base' }),
  );

  const validate = useCallback(() => {
    const errs = {};
    if (!campusName.trim()) {
      errs.campusName = 'Campus name is required.';
    } else if (!namePattern.test(campusName.trim())) {
      errs.campusName = 'Only letters, numbers, spaces, hyphens, underscores, and periods.';
    }
    if (email.trim() && !emailPattern.test(email.trim())) {
      errs.email = 'Enter a valid email address.';
    }
    if (phone.trim() && !phonePattern.test(phone.trim())) {
      errs.phone = 'Enter a valid 10-digit phone number.';
    }
    if (zipcode.trim() && zipRegex) {
      const zr = new RegExp(`^${zipRegex}$`);
      if (!zr.test(zipcode.trim())) {
        errs.zipcode = 'Enter a valid zip code.';
      }
    }
    if (streetAddress.trim() && !/^[0-9a-zA-Z_\- .']+$/.test(streetAddress.trim())) {
      errs.streetAddress = 'Only letters, numbers, spaces, hyphens, underscores, apostrophes, and periods.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [campusName, email, phone, zipcode, zipRegex, streetAddress]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      campusName: campusName.trim(),
      corporateId: parseInt(corporateId, 10),
      country,
      streetAddress: streetAddress.trim(),
      city: city.trim(),
      state,
      zipcode: zipcode.trim(),
      timezoneId: parseInt(timezoneId, 10),
      email: email.trim(),
      phone: phone.trim(),
    });
  }, [validate, onSave, campusName, corporateId, country, streetAddress, city, state, zipcode, timezoneId, email, phone]);

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">Edit Campus Info</h2>
        <button className="dp-drawer__close" onClick={onClose} aria-label="Close drawer">
          <svg className="dp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form className="dp-drawer__body" onSubmit={handleSubmit}>
        {/* Campus Name */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            Campus Name <span className="dp-drawer__required">*</span>
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.campusName ? ' is-invalid' : ''}`}
            value={campusName}
            onChange={(e) => setCampusName(e.target.value)}
            placeholder="Campus name"
            autoFocus
          />
          {errors.campusName && <p className="dp-drawer__error">{errors.campusName}</p>}
        </div>

        {/* Corporation */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Corporation</label>
          <select
            className="dp-drawer__select"
            value={corporateId}
            onChange={(e) => setCorporateId(e.target.value)}
          >
            <option value="">Select corporation...</option>
            {sortedCorporations.map((c) => (
              <option key={c.corporate_id} value={c.corporate_id}>
                {c.corporate_name}
              </option>
            ))}
          </select>
        </div>

        {/* Country */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Country</label>
          <select
            className="dp-drawer__select"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            <option value="">Select country...</option>
            {countries.map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
        </div>

        {/* Street Address */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Street Address</label>
          <input
            type="text"
            className={`dp-drawer__input${errors.streetAddress ? ' is-invalid' : ''}`}
            value={streetAddress}
            onChange={(e) => setStreetAddress(e.target.value)}
            placeholder="Street address"
          />
          {errors.streetAddress && <p className="dp-drawer__error">{errors.streetAddress}</p>}
        </div>

        {/* City + State + Zip row */}
        <div className="crpp-drawer-address-row">
          <div className="dp-drawer__field" style={{ flex: '2' }}>
            <label className="dp-drawer__label">City</label>
            <input
              type="text"
              className="dp-drawer__input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
            />
          </div>
          <div className="dp-drawer__field" style={{ flex: '1' }}>
            <label className="dp-drawer__label">State</label>
            <select
              className="dp-drawer__select"
              value={state}
              onChange={(e) => setState(e.target.value)}
              disabled={!country}
            >
              <option value="">...</option>
              {states.map(([code]) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          <div className="dp-drawer__field" style={{ flex: '1' }}>
            <label className="dp-drawer__label">Zip Code</label>
            <input
              type="text"
              className={`dp-drawer__input${errors.zipcode ? ' is-invalid' : ''}`}
              value={zipcode}
              onChange={(e) => setZipcode(e.target.value)}
              placeholder="Zip"
            />
            {errors.zipcode && <p className="dp-drawer__error">{errors.zipcode}</p>}
          </div>
        </div>

        {/* Timezone */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Timezone</label>
          <select
            className="dp-drawer__select"
            value={timezoneId}
            onChange={(e) => setTimezoneId(e.target.value)}
          >
            <option value="">Select timezone...</option>
            {sortedTimezones.map((tz) => (
              <option key={tz.timezone_id} value={tz.timezone_id}>
                {tz.timezone_name} ({tz.timezone})
              </option>
            ))}
          </select>
        </div>

        {/* Email */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Email</label>
          <input
            type="text"
            className={`dp-drawer__input${errors.email ? ' is-invalid' : ''}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
          />
          {errors.email && <p className="dp-drawer__error">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">Phone</label>
          <input
            type="text"
            className={`dp-drawer__input${errors.phone ? ' is-invalid' : ''}`}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="10-digit phone number"
          />
          {errors.phone && <p className="dp-drawer__error">{errors.phone}</p>}
        </div>

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BasicInfoDrawer;
